/**
 * Direct-ARC route contract for `arc_gateway`.
 *
 * In DIRECT (single-ARC) mode the browser talks to one plain-HTTP origin on
 * port 80 — whatever host it typed, because the gateway binds `:80` with no
 * host matcher — and every backend is reached by path prefix instead of by
 * port. This file is the single source of truth for those prefixes; it mirrors
 * the table in `docs/plans/arc-gateway-on-device-proxy.md` and the route blocks
 * in `arc_gateway/Caddyfile`. Change all three together.
 *
 * Direct-mode URLs use the page origin. In development, setupProxy.js forwards
 * /api to REACT_APP_SINGLE_ARC_HOSTNAME so browser requests stay same-origin.
 * Fleet proxy URLs intentionally use the
 * fixed secure `https`/`wss` schemes even when the dev server page is plain
 * HTTP.
 *
 * In FLEET mode the same service names are nested below
 * `/arc/<serial>/api/...` on the proxy's default HTTPS origin. The proxy still
 * retains the old per-port/`?arc=` surface for existing clients, but new
 * webapp requests use the path contract below.
 *
 * EVERY gateway route lives under `/api`, including the WebSockets and
 * authentication. That is deliberate: `/api/*` is the gateway's namespace and
 * never falls through to the SPA, so unprefixed names like `/ros` or `/audio`
 * stay available to React Router. See the plan for the full reasoning.
 */

import { isOnlineMode } from "./webapp";

/**
 * Path prefixes as seen by the browser.
 *
 * Note the two shapes, which mirror how each backend expects to be addressed:
 *
 *  - Full service prefix (e.g. `missionStorage: "/api/mission-storage"`): the
 *    gateway strips the whole thing, so the backend sees `/graphql`.
 *  - Bare `/api` (e.g. `wifi: "/api"`): the backend serves at `/wifi/*` and
 *    must keep that path, so the gateway strips only `/api`. Callers append
 *    `/wifi/...` themselves, exactly as they do today.
 */
export const ARC_GATEWAY = {
    /** Same-origin base in production and through the development proxy. */
    origin: () => window.location.origin,

    // WebSockets
    ros: "/api/ros",
    janus: "/api/janus",
    webrtc: "/api/webrtc",
    webrtcConfig: "/api/webrtc-config",
    audio: "/api/audio",
    /** Mission track CRUD + play/stop (live_audio_sink HTTP :8766). */
    audioTracks: "/api/speaker-tracks",

    // Authentication (login/refresh/logout are public; the rest needs a JWT)
    arcAuth: "/api/auth",

    // Full-service-prefix APIs
    missionStorage: "/api/mission-storage",
    bbox: "/api/bbox",
    map: "/api/map",
    recorder: "/api/recorder",
    mapProgress: "/api/map-progress",
    system: "/api/system",
    gimbal: "/api/gimbal",
    insta360: "/api/insta360",
    nanoowl: "/api/nanoowl",
    thermal: "/api/thermal",
    missionScheduler: "/api/mission-scheduler",
    behaviour: "/api/behaviour",
    robotProfile: "/api/robot-profile",
    /** Local basemap tiles (osm_tile_server). Unprotected — Leaflet <img> loads. */
    osmTiles: "/api/osm-tiles",

    // APIs that keep their own leading service path
    wifi: "/api",
    ethernet: "/api",
    mobile: "/api",
    turn: "/api",
    nav2: "/api",
} as const;

export type ArcGatewayService = Exclude<keyof typeof ARC_GATEWAY, "origin">;

/**
 * True when this page is a direct single-ARC session rather than fleet mode.
 *
 * Derived from `isOnlineMode` so there is exactly one mode decision in the app.
 * `isOnlineMode` is the page scheme: fleet is always HTTPS, a direct session is
 * always plain HTTP through arc_gateway. See lib/consts/webapp.js.
 */
export const isDirectArcMode = (): boolean => !isOnlineMode;

/** Public path prefix for one fleet ARC. */
export function arcPathPrefix(arcId: string): string {
    return `/arc/${encodeURIComponent(arcId)}`;
}

const originForHost = (host: string): string => {
    const normalizedHost = host.trim().replace(/^[a-z][a-z\d+.-]*:\/\//i, "").replace(/\/+$/, "");
    // Fleet proxy traffic is always secure, including when the React dev
    // server itself is running on http://localhost.
    return `https://${normalizedHost}`;
};

/**
 * Base URL for a direct gateway service or a fleet path-routed service.
 * Returns `null` when the required origin is unavailable.
 *
 * Returning `null` rather than throwing keeps every call site a two-line change
 * and leaves the fleet path completely untouched.
 *
 * @param service key into ARC_GATEWAY
 * @param arcId     fleet ARC serial, if any.
 * @param proxyHost fleet proxy hostname when `arcId` is set.
 */
export function gatewayServiceBaseUrl(
    service: ArcGatewayService,
    arcId?: string | null,
    proxyHost?: string | null,
): string | null {
    if (typeof window === "undefined") return null;

    if (arcId) {
        if (!proxyHost?.trim()) return null;
        return `${originForHost(proxyHost)}${arcPathPrefix(arcId)}${ARC_GATEWAY[service]}`;
    }

    if (!isDirectArcMode()) return null;
    return `${ARC_GATEWAY.origin()}${ARC_GATEWAY[service]}`;
}

/**
 * WebSocket URL for a direct gateway service or a fleet path-routed service.
 *
 * Browsers cannot set an Authorization header on a WebSocket handshake, so the
 * ARC JWT is carried as `?token=`. The gateway's forward_auth accepts it there
 * (arc_auth's `_extract_token` reads Authorization, cookie, then `?token=`).
 *
 * Never append a token to static SPA asset URLs — only to gateway routes.
 */
export function gatewayWebSocketUrl(
    service: Extract<ArcGatewayService, "ros" | "janus" | "webrtc" | "audio">,
    token?: string | null,
    arcId?: string | null,
    proxyHost?: string | null,
): string | null {
    if (typeof window === "undefined") return null;

    const origin = arcId
        ? (proxyHost?.trim() ? originForHost(proxyHost) : null)
        : (isDirectArcMode() ? ARC_GATEWAY.origin() : null);
    if (!origin) return null;

    const scheme = arcId
        ? "wss:"
        : (window.location.protocol === "https:" ? "wss:" : "ws:");
    const wsOrigin = origin.replace(/^[a-z][a-z\d+.-]*:/i, scheme);
    const base = `${wsOrigin}${arcId ? arcPathPrefix(arcId) : ""}${ARC_GATEWAY[service]}`;
    return token ? `${base}?token=${encodeURIComponent(token)}` : base;
}
